import {
	addToTargetingExperiment,
	BaseServiceSetup,
	context,
	isExperimentEnabledSSE,
	utils,
} from '@wikia/ad-engine';
import { Injectable } from '@wikia/dependency-injection';

const logGroup = 'UcpDesktopStickyRightRailExperiment';

@Injectable()
export class UcpDesktopStickyRightRailExperimentSetup extends BaseServiceSetup {
	private readonly pathfinderExperimentVariantNames = {
		control: 'aden-14354-control',
		variant1: 'aden-14354-variant-1',
	};

	private readonly CONTENT_SELECTOR = '.mw-parser-output > p';

	call(): void {
		const experimentVariant = isExperimentEnabledSSE(
			this.instantConfig,
			'aden-14354',
			'stickyRightRail',
			logGroup,
		);

		if (experimentVariant) {
			this.configure(experimentVariant);
		}
	}

	private configureExperiment(): void {
		// Add a marker class to the body to adjust the right rail styling
		document.body.classList.add('sticky-right-rail-enabled');
		context.set('experiments.stickyRightRail', true);

		// TWEAK: support moving DiscordIntegratorModule whenever it loads
		const moveDiscordIntegratorModule = () => {
			utils.logger(logGroup, 'DiscordIntegratorModule hook, moving module');
			const el = document.querySelector('.DiscordIntegratorModule');
			if (!el) {
				utils.logger(logGroup, 'DiscordIntegratorModule not found, skipping');
				return;
			}
			el.classList.add('wds-viewability-slider__sticky');
			// add wrapper around DiscordIntegratorModule to allow it to be sticky
			const wrapper = document.createElement('div');
			wrapper.classList.add('wds-viewability-slider__module');
			wrapper.prepend(el as Node);
			const rail = document.getElementById('WikiaRail');
			if (!rail || !rail.parentElement) {
				utils.logger(logGroup, 'WikiaRail not found, skipping');
				return;
			}
			rail.parentElement.insertBefore(wrapper, rail);
			// ok, now we need to adjust #rail-boxad-wrapper to be slightly smaller
			const boxadWrapper = document.getElementById('rail-boxad-wrapper');
			if (boxadWrapper) {
				boxadWrapper.classList.add('shorter-height');
			}
		};

		if (document.querySelector('.DiscordIntegratorModule')) {
			moveDiscordIntegratorModule();
		} else {
			// @ts-ignore Use global MW API, it's all fine, TS checks are disabled
			if (window.mw && window.mw.hook) {
				utils.logger(logGroup, 'DiscordIntegratorModule hook found, using mw.hook');
				window.mw.hook('DiscordIntegrator.added').add(moveDiscordIntegratorModule);
			} else {
				// window.mw not found, fallback to using MutationObserver on #WikiaRail to detect when .DiscordIntegratorModule is added
				const rail = document.getElementById('WikiaRail');
				if (rail) {
					utils.logger(logGroup, 'DiscordIntegratorModule hook not found, using MutationObserver');
					const observer = new MutationObserver(() => {
						if (document.querySelector('.DiscordIntegratorModule')) {
							moveDiscordIntegratorModule();
							observer.disconnect();
						}
					});
					observer.observe(rail, { childList: true });
				} else {
					utils.logger(
						logGroup,
						'WikiaRail not found, DiscordIntegratorModule hook not found, skipping movig it',
					);
				}
			}
		}

		// TWEAK: add observer on body element and if there's .uap-sticked we need to add the css height of .top-ads-container as a --additional-top-margin value to .wds-viewablity-slider
		// #top_leaderboard slot classes:
		// 1) sticky has: gpt-ad ad-slot expanded-slot bfaa-template slot-responsive theme-hivi uap-toc-pusher
		// 2) normal has: gpt-ad ad-slot a9-ad expanded-slot sticky-tlb
		const slider = document.querySelector('.wds-viewability-slider') as HTMLElement;
		if (slider) {
			const adjustTopMargin = () => {
				let additionalTopMargin = 0;

				// check if there's `.uap-toc-pusher` on #top_leaderboard
				// AND if there's `.uap-sticked` on body
				const topLeaderboard = document.getElementById('top_leaderboard');
				if (
					topLeaderboard &&
					topLeaderboard.classList.contains('uap-toc-pusher') &&
					document.body.classList.contains('uap-sticked')
				) {
					additionalTopMargin = topLeaderboard.clientHeight;
				}
				slider.style.setProperty('--additional-top-margin', `${additionalTopMargin}px`);
			};
			const observer2 = new MutationObserver(adjustTopMargin);
			observer2.observe(document.body, { attributes: true, attributeFilter: ['class'] });
			observer2.observe(document.querySelector('.top-ads-container') as Node, {
				attributes: true,
				attributeFilter: ['class'],
				childList: true,
			});
			adjustTopMargin();
		}
	}

	private configureControl(): void {
		context.set('experiments.stickyRightRail', false);

		// we need to cleanup the sclasses from html because the layour breaks on smaller screen

		// remove all instances of .wds-viewablity-slider__module and .wds-viewablity-slider__sticky
		const removeClasses = () => {
			document.querySelectorAll('.wds-viewability-slider__module').forEach((el) => {
				el.classList.remove('wds-viewability-slider__module');
			});
			document.querySelectorAll('.wds-viewability-slider__sticky').forEach((el) => {
				el.classList.remove('wds-viewability-slider__sticky');
			});
		};
		removeClasses();
		// add observer on .WikiaRail to remove classes on mutation (for lazy loading modules)
		const observer = new MutationObserver(() => {
			removeClasses();
		});
		const rail = document.querySelector('.WikiaRail');
		if (rail) {
			utils.logger(logGroup, 'Adding cleanup observer to WikiaRail for lazy loading modules');
			observer.observe(rail, { childList: true });
		}
	}

	public configure(experimentVariant: string): void {
		if (this.isCharacterCountMoreThan6000()) {
			if (experimentVariant === 'variant1') {
				utils.logger(logGroup, 'Variant 1 triggered');
				addToTargetingExperiment(this.pathfinderExperimentVariantNames.variant1);
				this.configureExperiment();
			} else if (experimentVariant === 'control') {
				utils.logger(logGroup, 'Control triggered');
				addToTargetingExperiment(this.pathfinderExperimentVariantNames.control);
				this.configureControl();
			}
			return;
		}
		utils.logger(logGroup, 'Page is less than 6000 characters, skipping experiment');
		// cleanup some classes if they were added
		this.configureControl();
	}

	private isCharacterCountMoreThan6000(): boolean {
		const paragraphs = Array.from(document.querySelectorAll(this.CONTENT_SELECTOR));
		let characterCount = 0;

		for (const paragraph of paragraphs) {
			characterCount += paragraph.textContent?.length ?? 0;

			if (characterCount >= 6000) {
				return true;
			}
		}

		return false;
	}
}
